import React, { useState, useEffect, useRef } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import FormInput from '../../components/ui/FormInput';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/Themecontext';
import {
  saveAccount, uploadAccountImage,
  updateAuthProfile, updateAuthEmail, updateAuthPassword,
} from '../../services/accountService';

const TABS = [
  { key: 'perfil', label: '👤 Perfil', icon: '👤' },
  { key: 'acesso', label: '🔒 Acesso', icon: '🔒' },
  { key: 'whitelabel', label: '🎨 White Label', icon: '🎨' },
];

const COLOR_PRESETS = [
  { nome: 'Padrão', primaria: '#0B1D35', acento: '#00D68F', fundo: '#F7F8FA' },
  { nome: 'Azul Royal', primaria: '#1A237E', acento: '#448AFF', fundo: '#F5F7FF' },
  { nome: 'Vinho', primaria: '#3E0A1E', acento: '#E91E63', fundo: '#FFF5F7' },
  { nome: 'Esmeralda', primaria: '#0D2818', acento: '#2ECC71', fundo: '#F0FFF4' },
  { nome: 'Laranja', primaria: '#1A1A2E', acento: '#FF6B35', fundo: '#FFF8F5' },
  { nome: 'Roxo', primaria: '#1A0533', acento: '#A855F7', fundo: '#FAF5FF' },
];

function maskTelefone(value) {
  const digits = String(value).replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

export default function ContaPage() {
  const { user } = useAuth();
  const { account, updateAccount } = useTheme();
  const [tab, setTab] = useState('perfil');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const logoRef = useRef(null);
  const faviconRef = useRef(null);

  // Perfil
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [telefone, setTelefone] = useState('');

  // Acesso
  const [novoEmail, setNovoEmail] = useState('');
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');

  // White Label
  const [corPrimaria, setCorPrimaria] = useState('#0B1D35');
  const [corAcento, setCorAcento] = useState('#00D68F');
  const [corTexto, setCorTexto] = useState('#1E293B');
  const [corFundo, setCorFundo] = useState('#F7F8FA');
  const [corSidebar, setCorSidebar] = useState('#0B1D35');
  const [corSidebarTexto, setCorSidebarTexto] = useState('#94A3B8');
  const [corSidebarAtivo, setCorSidebarAtivo] = useState('#00D68F');
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');

  useEffect(() => {
    if (!account) return;
    setNomeEmpresa(account.nomeEmpresa || '');
    setNomeUsuario(account.nomeUsuario || user?.displayName || '');
    setTelefone(account.telefone || '');
    setCorPrimaria(account.corPrimaria || '#0B1D35');
    setCorAcento(account.corAcento || '#00D68F');
    setCorTexto(account.corTexto || '#1E293B');
    setCorFundo(account.corFundo || '#F7F8FA');
    setCorSidebar(account.corSidebar || '#0B1D35');
    setCorSidebarTexto(account.corSidebarTexto || '#94A3B8');
    setCorSidebarAtivo(account.corSidebarAtivo || '#00D68F');
    setLogoUrl(account.logoUrl || '');
    setFaviconUrl(account.faviconUrl || '');
  }, [account, user]);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  // ======== SALVAR PERFIL ========
  const handleSavePerfil = async () => {
    setSaving(true);
    try {
      await saveAccount(user.uid, { nomeEmpresa, nomeUsuario, telefone });
      await updateAuthProfile(nomeUsuario);
      updateAccount({ nomeEmpresa, nomeUsuario, telefone });
      showMsg('Perfil atualizado com sucesso!');
    } catch (err) {
      showMsg(err.message, 'error');
    }
    setSaving(false);
  };

  // ======== SALVAR ACESSO ========
  const handleUpdateEmail = async () => {
    if (!novoEmail || !senhaAtual) return showMsg('Preencha e-mail e senha atual.', 'error');
    setSaving(true);
    try {
      await updateAuthEmail(novoEmail, senhaAtual);
      showMsg('E-mail atualizado!');
      setNovoEmail('');
      setSenhaAtual('');
    } catch (err) {
      showMsg(err.message, 'error');
    }
    setSaving(false);
  };

  const handleUpdatePassword = async () => {
    if (!senhaAtual || !novaSenha) return showMsg('Preencha todos os campos.', 'error');
    if (novaSenha !== confirmarSenha) return showMsg('As senhas não coincidem.', 'error');
    if (novaSenha.length < 6) return showMsg('Senha deve ter no mínimo 6 caracteres.', 'error');
    setSaving(true);
    try {
      await updateAuthPassword(senhaAtual, novaSenha);
      showMsg('Senha atualizada!');
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
    } catch (err) {
      showMsg(err.message, 'error');
    }
    setSaving(false);
  };

  // ======== UPLOAD IMAGENS ========
  const handleImageUpload = async (file, type) => {
    if (!file) return;
    setSaving(true);
    try {
      const url = await uploadAccountImage(user.uid, file, type);
      const update = type === 'logo' ? { logoUrl: url } : { faviconUrl: url };
      await saveAccount(user.uid, update);
      if (type === 'logo') setLogoUrl(url);
      else setFaviconUrl(url);
      updateAccount(update);
      showMsg(`${type === 'logo' ? 'Logo' : 'Favicon'} atualizado!`);
    } catch (err) {
      showMsg(err.message, 'error');
    }
    setSaving(false);
  };

  const handleRemoveImage = async (type) => {
    setSaving(true);
    try {
      const update = type === 'logo' ? { logoUrl: '' } : { faviconUrl: '' };
      await saveAccount(user.uid, update);
      if (type === 'logo') setLogoUrl('');
      else setFaviconUrl('');
      updateAccount(update);
      showMsg(`${type === 'logo' ? 'Logo' : 'Favicon'} removido!`);
    } catch (err) {
      showMsg(err.message, 'error');
    }
    setSaving(false);
  };
  const handleSaveWhiteLabel = async () => {
    setSaving(true);
    try {
      const data = {
        corPrimaria, corAcento, corTexto, corFundo,
        corSidebar, corSidebarTexto, corSidebarAtivo,
        logoUrl, faviconUrl,
      };
      await saveAccount(user.uid, data);
      updateAccount(data);
      showMsg('Tema atualizado!');
    } catch (err) {
      showMsg(err.message, 'error');
    }
    setSaving(false);
  };

  const applyPreset = (preset) => {
    setCorPrimaria(preset.primaria);
    setCorAcento(preset.acento);
    setCorFundo(preset.fundo);
    setCorSidebar(preset.primaria);
    setCorSidebarAtivo(preset.acento);
  };

  const sectionTitle = (text) => (
    <div style={{
      fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 16, marginTop: 8,
    }}>{text}</div>
  );

  const colorField = (label, value, setter) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
      <input
        type="color" value={value} onChange={(e) => setter(e.target.value)}
        style={{
          width: 36, height: 36, border: '2px solid var(--border)', borderRadius: 8,
          cursor: 'pointer', padding: 0, background: 'none',
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{value}</div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--white)', borderRadius: 12, padding: 4, border: '1px solid var(--border)' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '10px 16px', border: 'none', borderRadius: 10,
              fontFamily: 'var(--font)', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
              background: tab === t.key ? 'var(--accent)' : 'transparent',
              color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)',
              transition: 'all 0.2s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Message */}
      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: 10, marginBottom: 16, fontSize: '0.82rem', fontWeight: 600,
          background: msg.type === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(0,214,143,0.08)',
          color: msg.type === 'error' ? '#DC2626' : '#059669',
          border: `1px solid ${msg.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(0,214,143,0.2)'}`,
        }}>
          {msg.type === 'error' ? '⚠️' : '✅'} {msg.text}
        </div>
      )}

      {/* ======== PERFIL ======== */}
      {tab === 'perfil' && (
        <Card style={{ padding: 28 }}>
          {sectionTitle('Informações da Empresa')}
          <FormInput label="Nome da Empresa" value={nomeEmpresa} onChange={(e) => setNomeEmpresa(e.target.value)} placeholder="Minha Empresa" />

          {sectionTitle('Dados Pessoais')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormInput label="Nome Completo" value={nomeUsuario} onChange={(e) => setNomeUsuario(e.target.value)} placeholder="Seu nome" />
            <FormInput label="Telefone" value={telefone} onChange={(e) => setTelefone(maskTelefone(e.target.value))} placeholder="(61) 99999-9999" maxLength={15} />
          </div>

          <div style={{
            padding: '14px 18px', background: 'var(--surface)', borderRadius: 10,
            border: '1px solid var(--border)', marginTop: 8, marginBottom: 20,
          }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 4 }}>E-mail de login</div>
            <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{user?.email}</div>
          </div>

          {sectionTitle('Plano Atual')}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', background: 'rgba(0,214,143,0.06)', borderRadius: 10,
            border: '1px solid rgba(0,214,143,0.15)', marginBottom: 20,
          }}>
            <div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--accent)' }}>
                Plano {account?.plano || 'Simulador'}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                Acesso completo ao simulador e CRM
              </div>
            </div>
            <span style={{
              fontSize: '0.68rem', fontWeight: 700, padding: '4px 12px', borderRadius: 100,
              background: 'var(--accent)', color: 'var(--primary)',
            }}>Ativo</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="accent" onClick={handleSavePerfil} disabled={saving}>
              {saving ? 'Salvando...' : '💾 Salvar Perfil'}
            </Button>
          </div>
        </Card>
      )}

      {/* ======== ACESSO ======== */}
      {tab === 'acesso' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card style={{ padding: 28 }}>
            {sectionTitle('Alterar E-mail')}
            <FormInput label="Novo E-mail" type="email" value={novoEmail} onChange={(e) => setNovoEmail(e.target.value)} placeholder="novo@email.com" />
            <FormInput label="Senha Atual (confirmação)" type="password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} placeholder="••••••••" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Button variant="accent" onClick={handleUpdateEmail} disabled={saving}>
                {saving ? 'Atualizando...' : '📧 Alterar E-mail'}
              </Button>
            </div>
          </Card>

          <Card style={{ padding: 28 }}>
            {sectionTitle('Alterar Senha')}
            <FormInput label="Senha Atual" type="password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} placeholder="••••••••" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormInput label="Nova Senha" type="password" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} placeholder="Mínimo 6 caracteres" />
              <FormInput label="Confirmar Senha" type="password" value={confirmarSenha} onChange={(e) => setConfirmarSenha(e.target.value)} placeholder="Repita a nova senha" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Button variant="accent" onClick={handleUpdatePassword} disabled={saving}>
                {saving ? 'Atualizando...' : '🔑 Alterar Senha'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ======== WHITE LABEL ======== */}
      {tab === 'whitelabel' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Logo e Favicon */}
          <Card style={{ padding: 28 }}>
            {sectionTitle('Logo e Favicon')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Logo */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Logo</span>
                  {logoUrl && (
                    <button onClick={() => handleRemoveImage('logo')} style={{
                      fontSize: '0.65rem', fontWeight: 600, color: '#F43F5E', background: 'rgba(244,63,94,0.08)',
                      border: '1px solid rgba(244,63,94,0.2)', borderRadius: 6, padding: '3px 8px',
                      cursor: 'pointer', fontFamily: 'var(--font)',
                    }}>✕ Remover</button>
                  )}
                </div>
                <div
                  onClick={() => logoRef.current?.click()}
                  style={{
                    width: '100%', height: 100, borderRadius: 12,
                    border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', background: 'var(--surface)', overflow: 'hidden',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" style={{ maxHeight: 80, maxWidth: '90%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Clique para enviar</span>
                  )}
                </div>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e.target.files[0], 'logo')} />
              </div>

              {/* Favicon */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Favicon</span>
                  {faviconUrl && (
                    <button onClick={() => handleRemoveImage('favicon')} style={{
                      fontSize: '0.65rem', fontWeight: 600, color: '#F43F5E', background: 'rgba(244,63,94,0.08)',
                      border: '1px solid rgba(244,63,94,0.2)', borderRadius: 6, padding: '3px 8px',
                      cursor: 'pointer', fontFamily: 'var(--font)',
                    }}>✕ Remover</button>
                  )}
                </div>
                <div
                  onClick={() => faviconRef.current?.click()}
                  style={{
                    width: '100%', height: 100, borderRadius: 12,
                    border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', background: 'var(--surface)', overflow: 'hidden',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  {faviconUrl ? (
                    <img src={faviconUrl} alt="Favicon" style={{ maxHeight: 48, maxWidth: 48, objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Clique para enviar</span>
                  )}
                </div>
                <input ref={faviconRef} type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e.target.files[0], 'favicon')} />
              </div>
            </div>
          </Card>

          {/* Presets */}
          <Card style={{ padding: 28 }}>
            {sectionTitle('Temas Prontos')}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {COLOR_PRESETS.map((p) => (
                <button
                  key={p.nome}
                  onClick={() => applyPreset(p)}
                  style={{
                    padding: '10px 14px', border: '2px solid var(--border)', borderRadius: 10,
                    background: 'var(--white)', cursor: 'pointer', fontFamily: 'var(--font)',
                    display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = p.acento}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ display: 'flex', gap: 3 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: p.primaria }} />
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: p.acento }} />
                    <div style={{ width: 14, height: 14, borderRadius: 4, background: p.fundo, border: '1px solid #ddd' }} />
                  </div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>{p.nome}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Cores customizadas */}
          <Card style={{ padding: 28 }}>
            {sectionTitle('Cores Customizadas')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {colorField('Cor Primária (Sidebar)', corPrimaria, setCorPrimaria)}
              {colorField('Cor de Acento', corAcento, setCorAcento)}
              {colorField('Cor do Texto', corTexto, setCorTexto)}
              {colorField('Cor de Fundo', corFundo, setCorFundo)}
              {colorField('Sidebar — Texto', corSidebarTexto, setCorSidebarTexto)}
              {colorField('Sidebar — Item Ativo', corSidebarAtivo, setCorSidebarAtivo)}
            </div>
          </Card>

          {/* Preview */}
          <Card style={{ padding: 28 }}>
            {sectionTitle('Pré-visualização')}
            <div style={{
              borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)',
              display: 'grid', gridTemplateColumns: '180px 1fr', height: 200,
            }}>
              {/* Sidebar preview */}
              <div style={{ background: corPrimaria || corSidebar, padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                  {logoUrl ? (
                    <img src={logoUrl} alt="" style={{ height: 20, objectFit: 'contain' }} />
                  ) : (
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: corAcento, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: '#fff', fontWeight: 700 }}>
                      {(nomeEmpresa || 'S')[0]}
                    </div>
                  )}
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>{nomeEmpresa || 'SimulaBank'}</span>
                </div>
                {['Dashboard', 'Simulador', 'Clientes'].map((item, i) => (
                  <div key={item} style={{
                    padding: '6px 10px', borderRadius: 6, fontSize: '0.68rem', fontWeight: 600, marginBottom: 4,
                    background: i === 0 ? `${corSidebarAtivo}22` : 'transparent',
                    color: i === 0 ? corSidebarAtivo : corSidebarTexto,
                  }}>{item}</div>
                ))}
              </div>
              {/* Content preview */}
              <div style={{ background: corFundo, padding: 16 }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: corTexto, marginBottom: 12 }}>Dashboard</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[1, 2].map((n) => (
                    <div key={n} style={{
                      background: '#fff', borderRadius: 8, padding: 12,
                      border: '1px solid rgba(0,0,0,0.06)',
                    }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: corAcento }}>{n * 24}</div>
                      <div style={{ fontSize: '0.6rem', color: corTexto, opacity: 0.5 }}>Métrica {n}</div>
                    </div>
                  ))}
                </div>
                <div style={{
                  marginTop: 10, padding: '6px 14px', borderRadius: 6,
                  background: corAcento, color: corPrimaria, fontSize: '0.68rem',
                  fontWeight: 700, display: 'inline-block',
                }}>Botão Ação</div>
              </div>
            </div>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="accent" onClick={handleSaveWhiteLabel} disabled={saving}>
              {saving ? 'Salvando...' : '🎨 Salvar Tema'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}